import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { useContext } from "react";
import { Map } from "schemas/functions";

import MergeTagChip from "components/Editor/MergeTagWarning/MergeTagChip";
import QuestionChip from "components/Styled/QuestionChip";
import { Template } from "features/Influencer/ContactList/schema";
import { containsMergeTagsTemplates } from "utils/string";

interface Props {
  loading: boolean;
  open: boolean;
  handleClose: () => void;
  template: Template;
  handleConfirm: (settings: Map) => void;
  settings: Map;
  setSettings: React.Dispatch<React.SetStateAction<Map>>;
}

const PreSaveDialog = ({
  open,
  loading,
  handleClose,
  template,
  handleConfirm,
  settings,
  setSettings,
}: Props) => {
  const { isEdited } = useContext(QuickSendDrawerContext);
  const hasMergeTag = containsMergeTagsTemplates(template?.body || "");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography textAlign={"center"} variant="h6">
          Before we save your templates 👀
        </Typography>
      </DialogTitle>

      <DialogContent>
        {!hasMergeTag && (
          <Box
            sx={{
              p: 2,
              borderRadius: 4,
              border: `1px solid ${grey[300]}`,
              mb: 2,
            }}
          >
            <Typography gutterBottom>Personalize your email</Typography>

            <Typography variant="caption" paragraph color="textSecondary">
              Warning: Using the merge tags like{" "}
              <MergeTagChip mergeTag="{First Name}" /> and{" "}
              <MergeTagChip mergeTag="{Brand Name}" /> helps personalizing your
              outreach. Keep editing?
            </Typography>
          </Box>
        )}

        {Boolean(isEdited || template?.attachedScheduledEmailsCount) && (
          <Box
            sx={{
              p: 2,
              borderRadius: 4,
              border: `1px solid ${grey[300]}`,
              mb: 2,
            }}
          >
            <Typography gutterBottom>Updating scheduled drafts</Typography>

            <Typography variant="caption" paragraph color="textSecondary">
              You have{" "}
              {template?.attachedScheduledEmailsCount ? (
                <>
                  {" "}
                  <strong>
                    {template?.attachedScheduledEmailsCount} scheduled emails
                  </strong>
                </>
              ) : (
                ""
              )}{" "}
              {isEdited && template?.attachedScheduledEmailsCount ? "and " : ""}
              {isEdited ? "an open draft" : ""} using this template. Update all
              pending emails with the latest changes?
            </Typography>

            <Grid container gap={1}>
              <QuestionChip
                isSelected={settings?.updatePendingEmails}
                onClick={() => {
                  setSettings((prev) => ({
                    ...prev,
                    updatePendingEmails: true,
                  }));
                }}
                label="Yes"
              />
              <QuestionChip
                isSelected={!settings?.updatePendingEmails}
                onClick={() => {
                  setSettings((prev) => ({
                    ...prev,
                    updatePendingEmails: false,
                  }));
                }}
                label="No"
              />
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button sx={{ color: "purple.main" }} onClick={handleClose}>
          Keep Editing
        </Button>
        <LoadingButton
          variant="contained"
          disableElevation
          sx={{ px: 2 }}
          onClick={() => handleConfirm(settings)}
          loading={loading}
        >
          Proceed
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PreSaveDialog;
